/**
 * oMessage Hero Animations
 * GSAP-powered animations for the hero section
 */

import { gsap } from 'gsap';

/**
 * Initialize hero section animations
 */
export function initHeroAnimations() {
  const hero = document.querySelector('.hero');
  if (!hero) return;

  // Create main timeline
  const tl = gsap.timeline({
    defaults: {
      ease: 'power3.out',
    },
  });

  // Animate hero elements with staggered timing
  const fadeUpElements = hero.querySelectorAll('.hero-animate-fade-up');
  const scaleInElements = hero.querySelectorAll('.hero-animate-scale-in');

  // Set initial states
  gsap.set(fadeUpElements, { y: 30, opacity: 0 });
  gsap.set(scaleInElements, { scale: 0.9, opacity: 0, y: 20 });

  // Build the animation timeline
  tl.to(fadeUpElements, {
    y: 0,
    opacity: 1,
    duration: 0.8,
    stagger: 0.1,
  })
  .to(scaleInElements, {
    scale: 1,
    opacity: 1,
    y: 0,
    duration: 1,
    ease: 'power2.out',
  }, '-=0.4');

  // Animate floating bubbles
  initFloatingBubbles();

  // Animate chat items in phone mockup
  initChatItemAnimations();

  // Add hover interactions
  initHeroInteractions();
}

/**
 * Floating bubble animations with randomized timing
 */
function initFloatingBubbles() {
  const bubbles = document.querySelectorAll('.floating-bubble');

  bubbles.forEach((bubble, index) => {
    // Initial entrance animation
    gsap.from(bubble, {
      opacity: 0,
      scale: 0.8,
      y: 20,
      duration: 0.6,
      delay: 0.8 + (index * 0.15),
      ease: 'back.out(1.7)',
    });

    // Continuous floating animation (already in CSS, but can enhance with GSAP)
    // This adds subtle rotation variation
    gsap.to(bubble, {
      rotation: gsap.utils.random(-3, 3),
      duration: gsap.utils.random(2, 4),
      repeat: -1,
      yoyo: true,
      ease: 'sine.inOut',
    });
  });
}

/**
 * Chat item entrance animations
 */
function initChatItemAnimations() {
  const chatItems = document.querySelectorAll('.chat-item');

  if (chatItems.length === 0) return;

  gsap.from(chatItems, {
    x: -20,
    opacity: 0,
    duration: 0.5,
    stagger: 0.1,
    delay: 1.2,
    ease: 'power2.out',
  });
}

/**
 * Interactive hover effects
 */
function initHeroInteractions() {
  // Button hover effects
  const buttons = document.querySelectorAll('.hero .btn');

  buttons.forEach(btn => {
    btn.addEventListener('mouseenter', () => {
      gsap.to(btn, {
        scale: 1.02,
        duration: 0.2,
        ease: 'power2.out',
      });
    });

    btn.addEventListener('mouseleave', () => {
      gsap.to(btn, {
        scale: 1,
        duration: 0.2,
        ease: 'power2.out',
      });
    });
  });

  // Phone mockup tilt effect on mouse move
  const phone = document.querySelector('.phone-showcase__device');
  const hero = document.querySelector('.hero');

  if (phone && hero) {
    hero.addEventListener('mousemove', (e) => {
      const rect = hero.getBoundingClientRect();
      const centerX = rect.width / 2;
      const centerY = rect.height / 2;
      const mouseX = e.clientX - rect.left;
      const mouseY = e.clientY - rect.top;

      const rotateY = ((mouseX - centerX) / centerX) * 5;
      const rotateX = ((centerY - mouseY) / centerY) * 5;

      gsap.to(phone, {
        rotateY: -5 + rotateY,
        rotateX: 2 + rotateX,
        duration: 0.5,
        ease: 'power2.out',
      });
    });

    hero.addEventListener('mouseleave', () => {
      gsap.to(phone, {
        rotateY: -5,
        rotateX: 2,
        duration: 0.8,
        ease: 'power2.out',
      });
    });
  }

  // Story avatar pulse on hover
  const storyAvatars = document.querySelectorAll('.story-avatar:not(.story-avatar--add)');

  storyAvatars.forEach(avatar => {
    avatar.addEventListener('mouseenter', () => {
      gsap.to(avatar, {
        scale: 1.1,
        duration: 0.3,
        ease: 'back.out(1.7)',
      });
    });

    avatar.addEventListener('mouseleave', () => {
      gsap.to(avatar, {
        scale: 1,
        duration: 0.3,
        ease: 'power2.out',
      });
    });
  });
}

/**
 * Parallax effect for gradient orbs based on scroll
 */
export function initHeroParallax() {
  const orbs = document.querySelectorAll('.gradient-orb');

  if (orbs.length === 0) return;

  window.addEventListener('scroll', () => {
    const scrollY = window.scrollY;

    orbs.forEach((orb, index) => {
      const speed = 0.1 + (index * 0.05);
      gsap.to(orb, {
        y: scrollY * speed,
        duration: 0.3,
        ease: 'none',
      });
    });
  });
}

/**
 * Counter animation for stats
 */
export function animateStats() {
  const stats = document.querySelectorAll('.stat__value');

  stats.forEach(stat => {
    const text = stat.textContent.trim();
    const hasPlus = text.includes('+');
    const hasM = text.includes('M');
    const hasK = text.includes('K');
    const hasStar = text.includes('★');
    const numericValue = parseFloat(text.replace(/[^0-9.]/g, ''));

    if (isNaN(numericValue)) return;

    // Store the target value and determine decimal places
    const hasDecimal = text.includes('.') || hasM;
    const decimalPlaces = hasDecimal ? 1 : 0;

    // Create counter object for GSAP to animate
    const counter = { value: 0 };

    // Set up counter animation with proper onUpdate
    gsap.to(counter, {
      value: numericValue,
      duration: 2.5,
      delay: 0.8,
      ease: 'power2.out',
      onUpdate: () => {
        let formatted = counter.value.toFixed(decimalPlaces);
        if (hasM) formatted += 'M';
        if (hasK) formatted += 'K';
        if (hasStar) formatted += '★';
        if (hasPlus) formatted += '+';
        stat.textContent = formatted;
      },
    });
  });
}
